import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, Trash2, ArrowRight, Scale, Pencil, X, Check, Share2, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const emptyForm = (members) => ({ description: "", amount: "", paid_by: members[0] || "", participants: [...members], date: "" });

export const SplitCosts = ({ trip, onRefresh }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [newMember, setNewMember] = useState("");
  const [form, setForm] = useState(emptyForm([]));
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  const apply = (d) => { setData(d); window.dispatchEvent(new Event("vr-split-refresh")); return d; };

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/trips/${trip.id}/split`);
      setData(d);
      setForm((f) => (f.description || f.amount ? f : emptyForm(d.members)));
    } catch (err) { console.warn("Kosten-verdeling laden mislukt", err); }
  }, [trip.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("vr-split-refresh", h);
    return () => window.removeEventListener("vr-split-refresh", h);
  }, [load]);

  if (!data) return null;
  const members = data.members || [];
  const expenses = data.expenses || [];
  const manualExpenses = expenses.filter((e) => (e.source || "manual") === "manual" && !e.extra_cost_id);

  const addMember = async () => {
    const name = newMember.trim();
    if (!name) return;
    if (members.some((m) => m.toLowerCase() === name.toLowerCase())) { setNewMember(""); return; }
    try {
      apply((await api.put(`/trips/${trip.id}/split/members`, { members: [...members, name] })).data);
      setNewMember("");
      setForm((f) => ({ ...f, paid_by: f.paid_by || name, participants: [...f.participants, name] }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeMember = async (name) => {
    try {
      const d = apply((await api.put(`/trips/${trip.id}/split/members`, { members: members.filter((m) => m !== name) })).data);
      if (d.members.includes(name)) {
        const first = name.trim().toLowerCase().split(" ")[0];
        const targets = d.members.filter((m) => m !== name && m.toLowerCase().split(" ")[0] === first);
        const target = targets.length === 1 ? targets[0] : null;
        setRenaming(name);
        setRenameVal(target || name);
        toast.info(`"${name}" staat nog in uitgaven of terugbetalingen (als betaler óf deelnemer) en kan daarom niet weg`, {
          description: target
            ? `Tip: hernoem "${name}" naar "${target}" — dan worden ze samengevoegd en verdwijnt de dubbele naam overal (ook in de CSV-export).`
            : "Tip: hernoem deze naam via het potloodje naar de juiste deelnemer — dan wordt alles samengevoegd.",
          duration: 9000,
        });
      }
      setForm((f) => ({ ...f, participants: f.participants.filter((p) => p !== name), paid_by: f.paid_by === name ? (d.members[0] || "") : f.paid_by }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const dupPairs = members
    .filter((s) => !s.trim().includes(" "))
    .map((s) => {
      const t = members.filter((m) => m !== s && m.toLowerCase().split(" ")[0] === s.trim().toLowerCase());
      return t.length === 1 ? [s, t[0]] : null;
    })
    .filter(Boolean);

  const dedupeMembers = async () => {
    try {
      const d = apply((await api.post(`/trips/${trip.id}/split/members/dedupe`)).data);
      toast.success((d.merged_pairs || []).length
        ? `Samengevoegd: ${d.merged_pairs.map(([a, b]) => `"${a}" → "${b}"`).join(", ")} — alle uitgaven, saldo's en de CSV-export zijn bijgewerkt`
        : "Geen dubbele namen gevonden");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleParticipant = (name) => setForm((f) => ({
    ...f, participants: f.participants.includes(name) ? f.participants.filter((p) => p !== name) : [...f.participants, name],
  }));

  const renameMember = async () => {
    const nw = renameVal.trim();
    if (!nw || !renaming || nw === renaming) { setRenaming(null); return; }
    try {
      const d = apply((await api.put(`/trips/${trip.id}/split/members/rename`, { old_name: renaming, new_name: nw })).data);
      toast.success(d.merged
        ? `"${renaming}" is samengevoegd met "${nw}" — alle uitgaven en verdelingen staan nu op één naam`
        : `"${renaming}" heet nu overal "${nw}"`);
      setForm((f) => ({ ...f,
        paid_by: f.paid_by === renaming ? nw : f.paid_by,
        participants: f.participants.map((p) => (p === renaming ? nw : p)) }));
      setRenaming(null);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setForm({ description: e.description, amount: String(e.amount_eur), paid_by: e.paid_by, participants: [...e.participants], date: e.date || "" });
  };

  const cancelEdit = () => { setEditId(null); setForm(emptyForm(members)); };

  const saveExpense = async () => {
    const payload = {
      description: form.description.trim(), amount_eur: parseFloat(form.amount),
      paid_by: form.paid_by, participants: form.participants, date: form.date || null,
    };
    setSaving(true);
    try {
      const d = editId
        ? (await api.patch(`/trips/${trip.id}/split/expenses/${editId}`, payload)).data
        : (await api.post(`/trips/${trip.id}/split/expenses`, payload)).data;
      apply(d);
      toast.success(editId ? "Uitgave bijgewerkt" : "Uitgave toegevoegd");
      setEditId(null);
      setForm(emptyForm(d.members));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteExpense = async (id) => {
    try {
      apply((await api.delete(`/trips/${trip.id}/split/expenses/${id}`)).data);
      toast.success("Uitgave verwijderd");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const canSave = form.description.trim() && parseFloat(form.amount) > 0 && form.paid_by && form.participants.length > 0;

  const shareWhatsApp = () => {
    const lines = [`💶 Kostenoverzicht — ${trip.name}`, `Totaal: € ${(data.total || 0).toFixed(2)}`, "", "Saldo:"];
    Object.entries(data.balances || {}).forEach(([name, bal]) => {
      lines.push(`• ${name}: ${bal > 0.005 ? `krijgt € ${bal.toFixed(2)}` : bal < -0.005 ? `moet € ${Math.abs(bal).toFixed(2)}` : "staat gelijk"}`);
    });
    if ((data.settlements || []).length > 0) {
      lines.push("", "Zo staan we weer gelijk:");
      data.settlements.forEach((s) => lines.push(`• ${s.from} betaalt € ${s.amount.toFixed(2)} aan ${s.to}`));
    }
    lines.push("", "— gedeeld via VoltRoute ⚡");
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
  };

  return (
    <div className="mt-4 space-y-4" data-testid="split-section">
      <div className="bg-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <p className="font-heading font-bold text-sm flex items-center gap-1.5"><Scale className="w-4 h-4 text-[#3A6EA5]" /> Kosten verdelen</p>
          <span className="flex items-center gap-2">
            {data.total > 0 && <p data-testid="split-total" className="font-mono font-bold text-sm">€ {data.total.toFixed(2)}</p>}
            <button data-testid="who-pays-link" onClick={() => navigate(`/trip/${trip.id}/wie-betaalt-wat`)}
              className="rounded-full bg-[#3A6EA5]/10 hover:bg-[#3A6EA5]/20 text-[#3A6EA5] text-[11px] font-bold px-3 py-1.5 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
              Wie betaalt wat <ArrowRight className="w-3 h-3" />
            </button>
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Tricount-stijl: noteer wie wat betaalde en zie meteen wie wie moet terugbetalen. Ook reisgenoten met toegang tot de trip kunnen uitgaven toevoegen.</p>

        <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mb-2"><Users className="w-3.5 h-3.5" /> Deelnemers</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {members.map((m) => renaming === m ? (
            <span key={m} data-testid={`split-member-rename-form-${m}`} className="inline-flex items-center gap-1 bg-card border border-primary/40 rounded-full pl-2 pr-1 py-0.5 text-xs">
              <input data-testid={`split-member-rename-input-${m}`} value={renameVal} autoFocus maxLength={60}
                onChange={(e) => setRenameVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") renameMember(); if (e.key === "Escape") setRenaming(null); }}
                className="w-28 bg-transparent outline-none font-semibold" />
              <button data-testid={`split-member-rename-save-${m}`} onClick={renameMember} title="Naam opslaan"
                className="p-1 rounded-full bg-primary text-primary-foreground active:scale-95 transition-transform duration-150"><Check className="w-3 h-3" /></button>
              <button data-testid={`split-member-rename-cancel-${m}`} onClick={() => setRenaming(null)}
                className="p-1 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-150"><X className="w-3 h-3" /></button>
            </span>
          ) : (
            <span key={m} data-testid={`split-member-chip-${m}`} className="inline-flex items-center gap-1 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-xs font-semibold">
              {m}
              {trip.tricount_name_map?.[m] && trip.tricount_name_map[m] !== m && (
                <span data-testid={`split-member-tricount-${m}`} title={`Gekoppeld aan Tricount-lid "${trip.tricount_name_map[m]}"`}
                  className="font-normal text-[10px] text-[#3A6EA5]">→ {trip.tricount_name_map[m]}</span>
              )}
              {trip.tricount_url && !trip.tricount_name_map?.[m] && (
                <span data-testid={`split-member-tricount-warn-${m}`} title="Nog niet gekoppeld aan een Tricount-lid — doe dit via 'Namen koppelen' bij Onkosten & uitgaven" className="flex items-center">
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                </span>
              )}
              <button data-testid={`split-member-rename-${m}`} onClick={() => { setRenaming(m); setRenameVal(m); }} title="Naam wijzigen (wordt overal aangepast)"
                className="p-0.5 rounded-full hover:bg-card text-muted-foreground transition-[background-color] duration-200">
                <Pencil className="w-3 h-3" />
              </button>
              <button data-testid={`split-member-remove-${m}`} onClick={() => removeMember(m)} className="p-0.5 rounded-full hover:bg-red-50 text-red-400 transition-[background-color] duration-200">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {members.length === 0 && <p className="text-xs text-muted-foreground italic">Nog geen deelnemers — voeg de namen toe van iedereen die meebetaalt.</p>}
        </div>
        {dupPairs.length > 0 && (
          <div data-testid="dedupe-banner" className="mb-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-[#946200] font-semibold flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Dubbele namen gevonden: {dupPairs.map(([a, b]) => `"${a}" en "${b}"`).join(", ")}
            </p>
            <p className="text-[11px] text-[#946200]/80 mt-0.5">Dat zijn waarschijnlijk dezelfde personen — hierdoor kloppen de verdeling, saldo's en CSV-export niet. Met één klik voeg je alles samen.</p>
            <button data-testid="dedupe-button" onClick={dedupeMembers}
              className="mt-2 rounded-full bg-[#946200] hover:bg-[#7a5000] text-white text-xs font-bold px-4 py-1.5 active:scale-95 transition-[transform,background-color] duration-200">
              Voeg samen: {dupPairs.map(([a, b]) => `${a} → ${b}`).join(" · ")}
            </button>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mb-2">Alle uitgaven voeg je toe én bewerk je via <strong>"Onkosten & uitgaven"</strong> hierboven of via de chips bij <strong>"Per dag"</strong> — hier zie je alleen wie meedoet en de saldo's.</p>
        <div className="flex gap-2">
          <input data-testid="split-member-input" value={newMember} onChange={(e) => setNewMember(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addMember()} maxLength={60} placeholder="Naam (bv. Anke)"
            className="flex-1 min-w-0 bg-secondary rounded-xl px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
          <button data-testid="split-member-add" onClick={addMember} disabled={!newMember.trim()}
            className="rounded-full bg-primary hover:bg-[#1B4332] disabled:opacity-40 text-primary-foreground text-xs font-bold px-4 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
            <Plus className="w-3.5 h-3.5" /> Toevoegen
          </button>
        </div>
      </div>

      {members.length > 0 && editId && (
        <div className="bg-card rounded-2xl p-5" data-testid="split-expense-form">
          <p className="text-xs font-semibold text-muted-foreground mb-1">Uitgave bewerken</p>
          <p className="text-[11px] text-muted-foreground mb-2">
            Pas het bedrag, de betaler of de verdeling aan.
          </p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input data-testid="split-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={120} placeholder="Omschrijving (bv. diner)"
              className="col-span-2 sm:col-span-1 bg-secondary rounded-xl px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
            <input data-testid="split-amount-input" type="number" min="0" step="0.01" value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="Bedrag (€)"
              className="bg-secondary rounded-xl px-3.5 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
            <input data-testid="split-date-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="bg-secondary rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
          </div>
          <label className="block text-[11px] text-muted-foreground mb-2">Betaald door
            <select data-testid="split-paidby-select" value={form.paid_by} onChange={(e) => setForm((f) => ({ ...f, paid_by: e.target.value }))}
              className="block w-full mt-1 bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200">
              {members.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <p className="text-[11px] text-muted-foreground mb-1.5">Voor wie (gelijke verdeling)</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {members.map((m) => (
              <button key={m} data-testid={`split-participant-${m}`} onClick={() => toggleParticipant(m)}
                className={`rounded-full px-3 py-1 text-xs font-semibold border active:scale-95 transition-[background-color,border-color,color,transform] duration-200 ${
                  form.participants.includes(m) ? "bg-primary text-primary-foreground border-primary" : "bg-secondary/60 border-transparent text-muted-foreground"}`}>
                {m}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="split-expense-save" onClick={saveExpense} disabled={!canSave || saving}
              className="rounded-full bg-primary hover:bg-[#1B4332] disabled:opacity-40 text-primary-foreground text-xs font-bold py-2 px-5 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
              <Plus className="w-3.5 h-3.5" /> {saving ? "Opslaan..." : "Bijwerken"}
            </button>
            <button data-testid="split-expense-cancel" onClick={cancelEdit}
              className="rounded-full bg-secondary hover:bg-secondary/70 text-xs font-semibold py-2 px-4 transition-[background-color] duration-200">
              Annuleer
            </button>
          </div>
        </div>
      )}

      {manualExpenses.length > 0 && (
        <div className="bg-card rounded-2xl p-5" data-testid="split-expense-list">
          <p className="text-xs font-semibold text-muted-foreground mb-0.5">Losse verdeelde uitgaven ({manualExpenses.length})</p>
          <p className="text-[11px] text-muted-foreground mb-2">Deze staan buiten het tripbudget en zijn alleen hier te bewerken. Alle andere uitgaven vind je bij "Onkosten & uitgaven" en "Per dag".</p>
          <div className="space-y-0.5">
            {manualExpenses.map((e) => (
              <div key={e.id} data-testid={`split-expense-${e.id}`} className="flex items-center justify-between gap-2 border-b border-border/60 last:border-0 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {e.description}
                    {e.source === "budget_day" && (
                      <span data-testid={`split-budget-badge-${e.id}`} title="Pas aan via Budget → Per dag (potloodje)"
                        className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#2D6A4F] bg-[#2D6A4F]/10 rounded-full px-2 py-0.5 align-middle">
                        uit budget · dag {e.budget_day}
                      </span>
                    )}
                    {(e.source === "extra_cost" || e.extra_cost_id) && (
                      <span data-testid={`split-extra-badge-${e.id}`} title="Toegevoegd via Budget → Onkosten & uitgaven"
                        className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#B5838D] bg-[#B5838D]/10 rounded-full px-2 py-0.5 align-middle">
                        onkosten
                      </span>
                    )}
                    {e.source === "settlement" && (
                      <span data-testid={`split-settlement-badge-${e.id}`} title="Terugbetaling — afgevinkt via Wie betaalt wat"
                        className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[#2D6A4F] bg-[#95D5B2]/30 rounded-full px-2 py-0.5 align-middle">
                        terugbetaling
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {e.paid_by} betaalde{e.date ? ` · ${e.date}` : ""} · voor {e.participants.join(", ")}
                  </p>
                </div>
                <span className="flex items-center gap-1 shrink-0">
                  {trip.tricount_url && e.source !== "settlement" && e.source !== "budget_day" && (
                    trip.tricount_pushed?.[e.id] ? (
                      <span data-testid={`tricount-synced-${e.id}`} title="Gesynct met Tricount" className="flex items-center">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#2D6A4F]" />
                      </span>
                    ) : (
                      <span data-testid={`tricount-notsynced-${e.id}`} title="Nog niet in Tricount — sync via 'Verstuur naar Tricount' of zet auto-sync aan" className="flex items-center">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground/50" />
                      </span>
                    )
                  )}
                  <span className="font-mono font-bold text-sm">€ {e.amount_eur.toFixed(2)}</span>
                  {e.source === "budget_day" ? (
                    <span className="text-[10px] text-muted-foreground italic px-1" title="Pas aan via Budget → Per dag (potloodje)">via budget</span>
                  ) : (
                    <>
                      <button data-testid={`split-expense-edit-${e.id}`} onClick={() => startEdit(e)}
                        className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`split-expense-delete-${e.id}`} onClick={() => deleteExpense(e.id)}
                        className="p-1.5 rounded-full hover:bg-red-50 text-red-400 transition-[background-color] duration-200">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {expenses.length > 0 && (
        <div className="bg-card rounded-2xl p-5" data-testid="split-balances">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-muted-foreground">Saldo per persoon</p>
            <button data-testid="split-share-whatsapp" onClick={shareWhatsApp} title="Stuur het saldo-overzicht naar je reisgenoten"
              className="rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-bold px-3.5 py-1.5 flex items-center gap-1.5 active:scale-95 transition-[transform,background-color] duration-200">
              <Share2 className="w-3.5 h-3.5" /> Deel saldo via WhatsApp
            </button>
          </div>
          <div className="space-y-1 mb-4">
            {Object.entries(data.balances || {}).map(([name, bal]) => (
              <div key={name} data-testid={`split-balance-${name}`} className="flex items-center justify-between text-sm">
                <span>{name}</span>
                <span className={`font-mono font-bold ${bal > 0.005 ? "text-[#2D6A4F]" : bal < -0.005 ? "text-red-600" : "text-muted-foreground"}`}>
                  {bal > 0.005 ? `krijgt € ${bal.toFixed(2)}` : bal < -0.005 ? `moet € ${Math.abs(bal).toFixed(2)}` : "staat gelijk"}
                </span>
              </div>
            ))}
          </div>
          {(data.settlements || []).length > 0 && (
            <>
              <p className="text-xs font-semibold text-muted-foreground mb-2">Zo staan jullie weer gelijk</p>
              <div className="space-y-1.5">
                {data.settlements.map((s, i) => (
                  <div key={i} data-testid={`split-settlement-${i}`} className="flex items-center gap-2 bg-secondary/50 rounded-xl px-3.5 py-2 text-sm">
                    <span className="font-semibold">{s.from}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="font-semibold">{s.to}</span>
                    <span className="ml-auto font-mono font-bold">€ {s.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
